/**
 * Payroll Accounting Service
 * Handles automatic posting of payroll to General Ledger
 */

import { supabase } from '@/lib/supabase'
import { PayrollRun } from './payrollService'

export interface PayrollGLAccounts {
  salaries_expense_account_id: string
  employer_ssnit_expense_account_id: string
  employer_tier2_expense_account_id: string
  cash_bank_account_id: string
  ssnit_payable_account_id: string
  paye_payable_account_id: string
  tier2_payable_account_id: string
  tier3_payable_account_id?: string
}

export interface GLPostingResult {
  success: boolean
  journalEntryId?: string
  error?: string
}

export class PayrollAccountingService {

  /**
   * Post payroll to General Ledger
   * Creates journal entry with double-entry bookkeeping
   */
  static async postPayrollToGL(
    payrollRunId: string,
    churchId: string,
    userId: string,
    accounts: PayrollGLAccounts,
    postingDate?: string
  ): Promise<GLPostingResult> {
    try {
      // Get payroll run details
      const { data: payrollRun, error: runError } = await supabase
        .from('payroll_runs')
        .select('*')
        .eq('id', payrollRunId)
        .single()

      if (runError) throw runError
      if (!payrollRun) throw new Error('Payroll run not found')

      // Check if already posted
      if (payrollRun.is_posted_to_gl) {
        return {
          success: false,
          error: 'Payroll has already been posted to GL'
        }
      }

      // Ensure payroll is paid before posting
      if (payrollRun.status !== 'paid') {
        return {
          success: false,
          error: 'Payroll must be marked as paid before posting to GL'
        }
      }

      const effectivePostingDate = postingDate || payrollRun.payment_date || new Date().toISOString().split('T')[0]

      // Generate journal entry code
      const jeCode = `JE-PAY-${payrollRun.payroll_year}-${payrollRun.payroll_month.toString().padStart(2, '0')}`

      // Create journal entry header
      const { data: journalEntry, error: jeError } = await supabase
        .from('general_ledger_entries')
        .insert({
          church_id: churchId,
          entry_date: effectivePostingDate,
          entry_type: 'journal_entry',
          reference_number: jeCode,
          description: `Payroll for ${this.getMonthName(payrollRun.payroll_month)} ${payrollRun.payroll_year}`,
          status: 'posted',
          created_by: userId,
          posted_by: userId,
          posted_at: new Date().toISOString()
        })
        .select()
        .single()

      if (jeError) throw jeError

      // Prepare journal entry lines
      const jeLines = []

      // DEBIT: Salaries & Wages Expense
      jeLines.push({
        church_id: churchId,
        entry_id: journalEntry.id,
        account_id: accounts.salaries_expense_account_id,
        debit_amount: payrollRun.total_gross,
        credit_amount: 0,
        description: `Salaries & wages for ${this.getMonthName(payrollRun.payroll_month)} ${payrollRun.payroll_year}`
      })

      // DEBIT: Employer SSNIT Expense (13%)
      jeLines.push({
        church_id: churchId,
        entry_id: journalEntry.id,
        account_id: accounts.employer_ssnit_expense_account_id,
        debit_amount: payrollRun.total_employer_ssnit,
        credit_amount: 0,
        description: `Employer SSNIT contribution (13%)`
      })

      // DEBIT: Employer Tier 2 Expense (5%)
      if (payrollRun.total_tier2_employer > 0) {
        jeLines.push({
          church_id: churchId,
          entry_id: journalEntry.id,
          account_id: accounts.employer_tier2_expense_account_id,
          debit_amount: payrollRun.total_tier2_employer,
          credit_amount: 0,
          description: `Employer Tier 2 pension contribution (5%)`
        })
      }

      // CREDIT: Cash/Bank Account (net pay)
      jeLines.push({
        church_id: churchId,
        entry_id: journalEntry.id,
        account_id: accounts.cash_bank_account_id,
        debit_amount: 0,
        credit_amount: payrollRun.total_net_pay,
        description: `Net salary payment to employees`
      })

      // CREDIT: SSNIT Payable (employee 5.5% + employer 13%)
      const totalSSNIT = payrollRun.total_employee_ssnit + payrollRun.total_employer_ssnit
      jeLines.push({
        church_id: churchId,
        entry_id: journalEntry.id,
        account_id: accounts.ssnit_payable_account_id,
        debit_amount: 0,
        credit_amount: totalSSNIT,
        description: `SSNIT payable (Employee 5.5% + Employer 13%)`
      })

      // CREDIT: PAYE Tax Payable
      jeLines.push({
        church_id: churchId,
        entry_id: journalEntry.id,
        account_id: accounts.paye_payable_account_id,
        debit_amount: 0,
        credit_amount: payrollRun.total_paye,
        description: `PAYE tax payable to GRA`
      })

      // CREDIT: Tier 2 Payable (employee 5% + employer 5%)
      const totalTier2 = payrollRun.total_tier2_employee + payrollRun.total_tier2_employer
      if (totalTier2 > 0) {
        jeLines.push({
          church_id: churchId,
          entry_id: journalEntry.id,
          account_id: accounts.tier2_payable_account_id,
          debit_amount: 0,
          credit_amount: totalTier2,
          description: `Tier 2 pension payable (Employee 5% + Employer 5%)`
        })
      }

      // CREDIT: Tier 3 Payable (if any)
      if (payrollRun.total_tier3 > 0 && accounts.tier3_payable_account_id) {
        jeLines.push({
          church_id: churchId,
          entry_id: journalEntry.id,
          account_id: accounts.tier3_payable_account_id,
          debit_amount: 0,
          credit_amount: payrollRun.total_tier3,
          description: `Tier 3 pension payable`
        })
      }

      // CREDIT: Other deductions payable (if significant)
      if (payrollRun.total_other_deductions > 0) {
        // This would typically be split by deduction type
        // For now, we'll add it to the cash account to balance
        const currentCashCredit = jeLines.find(l => l.account_id === accounts.cash_bank_account_id)
        if (currentCashCredit) {
          currentCashCredit.credit_amount += payrollRun.total_other_deductions
          currentCashCredit.description = `Net salary payment and other deductions`
        }
      }

      // Insert all journal entry lines
      const { error: linesError } = await supabase
        .from('journal_entry_lines')
        .insert(jeLines)

      if (linesError) throw linesError

      // Calculate totals for verification
      const totalDebits = jeLines.reduce((sum, line) => sum + line.debit_amount, 0)
      const totalCredits = jeLines.reduce((sum, line) => sum + line.credit_amount, 0)

      // Verify the entry balances
      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        throw new Error(`Journal entry does not balance: Debits ${totalDebits} != Credits ${totalCredits}`)
      }

      // Update journal entry with totals
      await supabase
        .from('general_ledger_entries')
        .update({
          total_debit: totalDebits,
          total_credit: totalCredits
        })
        .eq('id', journalEntry.id)

      // Mark payroll as posted
      await supabase
        .from('payroll_runs')
        .update({
          journal_entry_id: journalEntry.id,
          is_posted_to_gl: true,
          posted_to_gl_at: new Date().toISOString(),
          status: 'posted'
        })
        .eq('id', payrollRunId)

      // Record SSNIT contribution
      await supabase
        .from('ssnit_contributions')
        .insert({
          church_id: churchId,
          payroll_run_id: payrollRunId,
          contribution_month: payrollRun.payroll_month,
          contribution_year: payrollRun.payroll_year,
          contribution_date: effectivePostingDate,
          total_employee_contribution: payrollRun.total_employee_ssnit,
          total_employer_contribution: payrollRun.total_employer_ssnit,
          total_contribution: totalSSNIT,
          payment_status: 'pending',
          created_by: userId
        })

      // Record PAYE tax
      await supabase
        .from('paye_tax_ledger')
        .insert({
          church_id: churchId,
          payroll_run_id: payrollRunId,
          tax_month: payrollRun.payroll_month,
          tax_year: payrollRun.payroll_year,
          total_paye: payrollRun.total_paye,
          total_taxable_income: 0, // Would need to calculate from payroll details
          payment_status: 'pending',
          created_by: userId
        })

      // Record Tier 2 pension
      if (totalTier2 > 0) {
        await supabase
          .from('pension_contributions')
          .insert({
            church_id: churchId,
            payroll_run_id: payrollRunId,
            contribution_month: payrollRun.payroll_month,
            contribution_year: payrollRun.payroll_year,
            tier2_employee: payrollRun.total_tier2_employee,
            tier2_employer: payrollRun.total_tier2_employer,
            tier2_total: totalTier2,
            tier3_employee: payrollRun.total_tier3,
            tier3_total: payrollRun.total_tier3,
            payment_status: 'pending'
          })
      }

      return {
        success: true,
        journalEntryId: journalEntry.id
      }
    } catch (error: any) {
      console.error('Error posting payroll to GL:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Reverse payroll GL posting (for corrections)
   */
  static async reversePayrollPosting(
    payrollRunId: string,
    churchId: string,
    userId: string,
    reason: string
  ): Promise<GLPostingResult> {
    try {
      // Get payroll run
      const { data: payrollRun, error: runError } = await supabase
        .from('payroll_runs')
        .select('*')
        .eq('id', payrollRunId)
        .single()

      if (runError) throw runError
      if (!payrollRun.journal_entry_id) {
        return {
          success: false,
          error: 'No GL posting found for this payroll'
        }
      }

      // Get original journal entry lines
      const { data: originalLines, error: linesError } = await supabase
        .from('journal_entry_lines')
        .select('*')
        .eq('entry_id', payrollRun.journal_entry_id)

      if (linesError) throw linesError

      // Create reversal journal entry
      const reversalCode = `JE-PAY-REV-${payrollRun.payroll_year}-${payrollRun.payroll_month.toString().padStart(2, '0')}`

      const { data: reversalEntry, error: revError } = await supabase
        .from('general_ledger_entries')
        .insert({
          church_id: churchId,
          entry_date: new Date().toISOString().split('T')[0],
          entry_type: 'journal_entry',
          reference_number: reversalCode,
          description: `Reversal: Payroll for ${this.getMonthName(payrollRun.payroll_month)} ${payrollRun.payroll_year} - ${reason}`,
          status: 'posted',
          created_by: userId,
          posted_by: userId,
          posted_at: new Date().toISOString()
        })
        .select()
        .single()

      if (revError) throw revError

      // Create reversal lines (swap debits and credits)
      const reversalLines = originalLines.map(line => ({
        church_id: churchId,
        entry_id: reversalEntry.id,
        account_id: line.account_id,
        debit_amount: line.credit_amount,  // Swap
        credit_amount: line.debit_amount,  // Swap
        description: `Reversal: ${line.description}`
      }))

      await supabase
        .from('journal_entry_lines')
        .insert(reversalLines)

      // Calculate totals
      const totalDebits = reversalLines.reduce((sum, line) => sum + line.debit_amount, 0)
      const totalCredits = reversalLines.reduce((sum, line) => sum + line.credit_amount, 0)

      await supabase
        .from('general_ledger_entries')
        .update({
          total_debit: totalDebits,
          total_credit: totalCredits
        })
        .eq('id', reversalEntry.id)

      // Update payroll run
      await supabase
        .from('payroll_runs')
        .update({
          is_posted_to_gl: false,
          status: 'paid'
        })
        .eq('id', payrollRunId)

      return {
        success: true,
        journalEntryId: reversalEntry.id
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Get GL accounts for payroll posting from settings
   */
  static async getPayrollGLAccounts(churchId: string): Promise<PayrollGLAccounts | null> {
    try {
      const { data, error } = await supabase
        .from('payroll_gl_settings')
        .select('*')
        .eq('church_id', churchId)
        .single()

      if (error || !data) {
        console.error('Error getting payroll GL accounts:', error)
        return null
      }

      return {
        salaries_expense_account_id: data.salaries_expense_account_id,
        employer_ssnit_expense_account_id: data.employer_ssnit_expense_account_id,
        employer_tier2_expense_account_id: data.employer_tier2_expense_account_id,
        cash_bank_account_id: data.cash_bank_account_id,
        ssnit_payable_account_id: data.ssnit_payable_account_id,
        paye_payable_account_id: data.paye_payable_account_id,
        tier2_payable_account_id: data.tier2_payable_account_id,
        tier3_payable_account_id: data.tier3_payable_account_id,
      }
    } catch (error) {
      console.error('Error getting payroll GL accounts:', error)
      return null
    }
  }

  /**
   * Update payroll GL account settings
   */
  static async updatePayrollGLAccounts(
    churchId: string,
    accounts: PayrollGLAccounts,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('payroll_gl_settings')
        .upsert({
          church_id: churchId,
          ...accounts,
          updated_by: userId,
          updated_at: new Date().toISOString()
        })

      if (error) throw error

      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Get month name from number
   */
  static getMonthName(month: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    return months[month - 1]
  }
}

export default PayrollAccountingService
